import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { TWButton, TWInput, TWSelect, ToggleSwitch } from "../../../components/ui";
import CopyButton from "./copy-button";
import {
  buildProvisioningPayload,
  canShowSetupQr,
  setupQrFileName,
} from "./setup-qr-payload";

const SSID_MAX = 32;
const PASSWORD_MAX = 63;
const SECURITY_OPTIONS = [
  { value: "WPA", label: "WPA / WPA2 / WPA3" },
  { value: "WEP", label: "WEP" },
  { value: "NONE", label: "Open (no password)" },
];
const STEPS = [
  "Factory reset the tablet.",
  "On the welcome screen, tap the same spot 6 times until the QR scanner opens.",
  "Scan this QR code with the tablet camera.",
  'Wait until the tablet says "Enrolled".',
  "If the tablet asks for permissions, tap Allow on the two prompts (accessibility and modify system settings).",
];
const WIFI_EMPTY = {
  ssid: "",
  password: "",
  security: "WPA",
  hidden: false,
  showPassword: false,
};

const MASK = "••••••••";
const PASSWORD_KEY = "android.app.extra.PROVISIONING_WIFI_PASSWORD";
const PASSWORD_HINT =
  "WPA and WEP networks need a password; pick Open for a network without one.";

const SECTION = "space-y-4 border-t border-slate-100 pt-4 dark:border-slate-700";
const REASON = "text-sm text-slate-500 dark:text-slate-400";

// The open block. Everything the installer types about Wi-Fi lives in this
// component's state only: it never leaves the browser, is cleared when the
// block is switched off, and goes with the component when the modal closes.
const SetupQrReady = ({ code, validUntil, storeName, role, release, apiOrigin }) => {
  const [on, setOn] = useState(false);
  const [wifi, setWifi] = useState(WIFI_EMPTY);
  const canvasWrap = useRef(null);

  const patch = (changes) => setWifi((prev) => ({ ...prev, ...changes }));
  const toggle = (next) => {
    setOn(next);
    if (!next) setWifi(WIFI_EMPTY);
  };

  const payload = useMemo(
    () =>
      buildProvisioningPayload({
        apkUrl: release.fileurl,
        sha256Hex: release.sha256,
        apiBase: apiOrigin,
        code,
        wifi: {
          ssid: wifi.ssid,
          password: wifi.password,
          security: wifi.security,
          hidden: wifi.hidden,
        },
      }),
    [release.fileurl, release.sha256, apiOrigin, code, wifi],
  );
  // The canvas always encodes the real payload. Only the Payload text (and its Copy) hides the
  // Wi-Fi password while Show is off.
  const maskPassword = !wifi.showPassword && PASSWORD_KEY in payload;
  const pretty = JSON.stringify(
    maskPassword ? { ...payload, [PASSWORD_KEY]: MASK } : payload,
    null,
    2,
  );

  const download = () => {
    const canvas = canvasWrap.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = setupQrFileName(role, storeName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isOpenNetwork = (security) => security === "NONE";
  const needsPassword =
    wifi.ssid.trim() !== "" && !isOpenNetwork(wifi.security) && wifi.password === "";

  return (
    <div className={`${SECTION} gl-block`}>
      <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
        <ToggleSwitch checked={on} onChange={toggle} ariaLabel="Show setup QR" />
        <span aria-hidden>Show setup QR</span>
      </label>
      {on ? (
        <div className="space-y-4">
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Wi-Fi for the tablet (optional)
            </legend>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Leave empty if the tablet is already on a network.
            </p>
            <TWInput
              label="Network name (SSID)"
              value={wifi.ssid}
              name="wifi-network"
              maxLength={SSID_MAX}
              autoComplete="off"
              onChange={(e) => patch({ ssid: e.target.value })}
            />
            <div className="flex items-end gap-2">
              <TWInput
                label="Password"
                type={wifi.showPassword ? "text" : "password"}
                name="wifi-secret"
                value={wifi.password}
                maxLength={PASSWORD_MAX}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                disabled={isOpenNetwork(wifi.security)}
                wrapperClassName="min-w-0 flex-1"
                onChange={(e) => patch({ password: e.target.value })}
              />
              <TWButton
                className="h-10"
                aria-pressed={wifi.showPassword}
                disabled={isOpenNetwork(wifi.security)}
                onClick={() => patch({ showPassword: !wifi.showPassword })}
              >
                {wifi.showPassword ? "Hide" : "Show"}
              </TWButton>
            </div>
            {needsPassword ? (
              <p className="-mt-1 text-xs text-slate-500 dark:text-slate-400">
                {PASSWORD_HINT}
              </p>
            ) : null}
            <TWSelect
              label="Security"
              options={SECURITY_OPTIONS}
              value={wifi.security}
              showSearch={false}
              allowClear={false}
              onChange={(value) =>
                patch(
                  isOpenNetwork(value)
                    ? { security: value, password: "", showPassword: false }
                    : { security: value },
                )
              }
            />
            <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
              <ToggleSwitch
                checked={wifi.hidden}
                onChange={(next) => patch({ hidden: next })}
                ariaLabel="Hidden network"
              />
              <span aria-hidden>Hidden network</span>
            </label>
          </fieldset>

          <div
            role="note"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-300"
          >
            Treat this QR like the Wi-Fi password: anyone who scans it can enrol
            a tablet. It stops working when the code expires or is revoked.
          </div>

          <div>
            <div ref={canvasWrap} className="flex justify-center">
              <div className="rounded-md border border-slate-200 bg-white p-2">
                <QRCodeCanvas
                  value={JSON.stringify(payload)}
                  size={400}
                  level="L"
                  includeMargin
                  data-testid="setup-qr"
                  role="img"
                  aria-label="Setup QR code for this tablet"
                  style={{ width: "100%", maxWidth: 400, height: "auto" }}
                />
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
              Valid until {validUntil}
            </p>
          </div>

          <TWButton className="w-full sm:w-auto" onClick={download}>
            Download PNG
          </TWButton>

          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-700 dark:text-slate-200">
            {STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
            <p>
              Generate a new QR after uploading a newer agent release; this one
              pins the current APK.
            </p>
            <p>Cameraless kiosks: use the wizard or enroll.sh.</p>
            <p>
              The agent release marked Current must be built for this
              environment: a test build for api-test, a release build for
              production.
            </p>
          </div>

          <details className="pb-2">
            <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-300">
              Payload
            </summary>
            {maskPassword ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Password hidden. Press Show to include it in the payload and in Copy.
              </p>
            ) : null}
            <div className="mt-2 space-y-2">
              <div className="flex justify-end">
                <CopyButton text={pretty} />
              </div>
              <pre
                data-testid="setup-qr-payload"
                tabIndex={0}
                aria-label="Setup QR payload"
                className="max-h-48 w-full select-text overflow-auto whitespace-pre rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {pretty}
              </pre>
            </div>
          </details>
        </div>
      ) : null}
    </div>
  );
};

// The setup QR block of the code modal (created state). Renders nothing while
// the releases load; a single reason line when no QR can be built (QR-7).
// Keyed by the code: a new code starts collapsed with the Wi-Fi fields empty.
const SetupQr = ({ code, validUntil, storeName, role, agentRelease, apiOrigin }) => {
  const status = agentRelease?.status;
  const release = agentRelease?.release;
  const verdict = canShowSetupQr({ status, release, apiOrigin });
  if (!verdict.ok) {
    return verdict.reason ? (
      <div className={SECTION}>
        <p className={REASON}>{verdict.reason}</p>
      </div>
    ) : null;
  }
  return (
    <SetupQrReady
      key={code}
      code={code}
      validUntil={validUntil}
      storeName={storeName}
      role={role}
      release={release}
      apiOrigin={apiOrigin}
    />
  );
};

export default SetupQr;
